import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ref, get, update, push, remove, database, onValue } from "../firebase";
import {
  FiArrowLeft,
  FiVolume2,
  FiVolumeX,
  FiShare2,
  FiDownload,
  FiLogIn,
  FiThumbsUp,
  FiThumbsDown,
  FiMessageSquare,
  FiSend,
  FiClock,
  FiUsers,
  FiExternalLink,
  FiEdit,
  FiCheck,
} from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";
import { toast } from "react-toastify";

const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;

async function fetchYouTubeVideoDetails(videoId) {
  try {
    const videoRes = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?part=snippet,statistics&id=${videoId}&key=${YOUTUBE_API_KEY}`
    );
    const videoData = await videoRes.json();
    if (!videoData.items || !videoData.items[0]) return null;
    const video = videoData.items[0];

    const channelId = video.snippet.channelId;
    const channelRes = await fetch(
      `https://www.googleapis.com/youtube/v3/channels?part=snippet,statistics&id=${channelId}&key=${YOUTUBE_API_KEY}`
    );
    const channelData = await channelRes.json();
    const channel = channelData.items && channelData.items[0];

    return {
      title: video.snippet.title,
      description: video.snippet.description,
      thumbnail: video.snippet.thumbnails.high.url,
      channelTitle: video.snippet.channelTitle,
      channelId,
      viewCount: video.statistics.viewCount,
      likeCount: video.statistics.likeCount,
      publishedAt: video.snippet.publishedAt,
      channelSubscribers: channel ? channel.statistics.subscriberCount : null,
      channelProfile: channel ? channel.snippet.thumbnails.default.url : null,
    };
  } catch (err) {
    // console.error('Error fetching YouTube video details:', err);
    return null;
  }
}

const VideoPlay = () => {
  const { videoId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const commentInputRef = useRef(null);
  const [videoData, setVideoData] = useState(null);
  const [userProfile, setUserProfile] = useState({});
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [subscribers, setSubscribers] = useState([]);
  const [isLiked, setIsLiked] = useState(false);
  const [isDisliked, setIsDisliked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [dislikeCount, setDislikeCount] = useState(0);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [watchTimeSeconds, setWatchTimeSeconds] = useState({
    video: 0,
    youtube: 0,
  });
  const [pointsEarned, setPointsEarned] = useState(0);
  const [lastAwardedTimeSeconds, setLastAwardedTimeSeconds] = useState({
    video: 0,
    youtube: 0,
  });
  const [showToast, setShowToast] = useState(null);
  const [isYouTubeVideo, setIsYouTubeVideo] = useState(false);
  const [youtubeVideoId, setYoutubeVideoId] = useState("");
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [giftConfig, setGiftConfig] = useState(null);
  const [commissionConfig, setCommissionConfig] = useState(null);
  const [isPhoto, setIsPhoto] = useState(false);
  const [photoData, setPhotoData] = useState(null);
  const [photoLikes, setPhotoLikes] = useState({});
  const [photoComments, setPhotoComments] = useState([]);
  const [hasLikedPhoto, setHasLikedPhoto] = useState(false);
  const [newPhotoComment, setNewPhotoComment] = useState("");
  const [photoLikeCount, setPhotoLikeCount] = useState(0);
  const [photoCommentCount, setPhotoCommentCount] = useState(0);
  const [photoWatchTimeSeconds, setPhotoWatchTimeSeconds] = useState(0);
  const [lastPhotoAwardedTimeSeconds, setLastPhotoAwardedTimeSeconds] =
    useState(0);
  const [directPhoto, setDirectPhoto] = useState(null);
  // Add a new state to persist the photo from navigation state and sessionStorage
  const [persistedPhoto, setPersistedPhoto] = useState(() => {
    if (location.state && location.state.photo) {
      sessionStorage.setItem("lastPhoto", JSON.stringify(location.state.photo));
      return location.state.photo;
    }
    const stored = sessionStorage.getItem("lastPhoto");
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  });
  const [commentCycleIndex, setCommentCycleIndex] = useState(0);
  const [ads, setAds] = useState([]);
  const [showAd, setShowAd] = useState(false);
  const [adProgress, setAdProgress] = useState(0);
  const [currentAd, setCurrentAd] = useState(null);
  const adTimerRef = useRef(null);
  const [canSkipAd, setCanSkipAd] = useState(false);
  const skipTimerRef = useRef(null);
  const [skipCountdown, setSkipCountdown] = useState(10);
  const [mainVideoTime, setMainVideoTime] = useState(0);
  const [adStarted, setAdStarted] = useState(false);
  const [adPlaybackTime, setAdPlaybackTime] = useState(0);
  const videoPlayerContainerRef = useRef(null);
  const [shareCount, setShareCount] = useState(0);

  // Reset ad states when videoId changes (for related video navigation)
  useEffect(() => {
    setAdStarted(false);
    setShowAd(false);
    setCurrentAd(null);
    setAdProgress(0);
    setCanSkipAd(false);
    setSkipCountdown(10);
    setMainVideoTime(0);
    setAdPlaybackTime(0);
  }, [videoId]);

  // On mount, update sessionStorage if navigation state changes
  useEffect(() => {
    if (location.state && location.state.photo) {
      sessionStorage.setItem("lastPhoto", JSON.stringify(location.state.photo));
      setPersistedPhoto(location.state.photo);
    }
  }, [location.state]);

  // Detect if this is a photo route
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("type") === "photo") {
      setIsPhoto(true);
    } else {
      setIsPhoto(false);
    }
  }, [location.search]);

  // Define getCurrentUser at the top so it is available everywhere
  const getCurrentUser = () => {
    const userData = JSON.parse(localStorage.getItem("currentUser"));
    return {
      mobile: userData?.mobile || "",
      username: userData?.username || userData?.mobile || "Anonymous",
      name: userData?.name || "",
      surname: userData?.surname || "",
    };
  };

  // Add useEffect to sync isLoggedIn with localStorage
  useEffect(() => {
    const checkLogin = () => {
      const userData = localStorage.getItem("currentUser");
      setIsLoggedIn(!!userData);
    };

    checkLogin();
    window.addEventListener("storage", checkLogin);
    return () => {
      window.removeEventListener("storage", checkLogin);
    };
  }, []);

  // Fetch gift and commission configurations
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const giftRef = ref(database, "videoplayer/Gifts/1");
        const commissionRef = ref(database, "videoplayer/Commissions/1");
        const [giftSnapshot, commissionSnapshot] = await Promise.all([
          get(giftRef),
          get(commissionRef),
        ]);

        if (giftSnapshot.exists()) {
          setGiftConfig(giftSnapshot.val());
        } else {
          setGiftConfig({
            watchPoints: 0,
            sharePoints: 0,
            likePoints: 0,
            unlikePoints: 0,
            invitePoints: 0,
            purchasePoints: 0,
            subscriptionPoints: 0,
          });
        }

        if (commissionSnapshot.exists()) {
          setCommissionConfig(commissionSnapshot.val());
        } else {
          setCommissionConfig({
            watchVideoPoints: 0,
            watchVideoTime: 0,
            youtubePoints: 0,
            youtubeTime: 0,
            videoPoints: 0,
            instagramPoints: 0,
            instagramTime: 0,
            facebookPoints: 0,
            facebookTime: 0,
            googlePoints: 0,
            googleTime: 0,
            photoTime: 0,
            photoPoints: 0,
          });
        }
      } catch (err) {
        // console.error('Error fetching configurations:', err);
        toast.error("Failed to load configurations.");
      }
    };
    fetchConfigs();
  }, []);

  // Fetch video data
  useEffect(() => {
    const fetchVideoData = async () => {
      setAds([]); // Clear ads immediately when starting fetch
      try {
        const videosRef = ref(database, "videoplayer/videoUpload");
        const snapshot = await get(videosRef);

        if (snapshot.exists()) {
          let foundVideo = null;
          let userMobile = "";
          const videoData = snapshot.val();

          Object.entries(videoData).some(([mobile, userVideos]) => {
            return Object.entries(userVideos).some(([videoKey, video]) => {
              const generatedId =
                video.id ||
                `${mobile.replace(/[^0-9]/g, "_")}_${new Date(
                  video.uploadDate || Date.now()
                )
                  .getTime()
                  .toString(36)}`;
              if (generatedId === videoId) {
                foundVideo = {
                  id: generatedId,
                  dbKey: videoKey,
                  type: "video",
                  title: video.title || "Untitled Video",
                  description: video.description || "",
                  thumbnail:
                    video.thumbnailURL ||
                    "https://via.placeholder.com/320x180?text=No+Thumbnail",
                  videoURL: video.videoURL || "",
                  views: video.views || 0,
                  likes: video.likes || {},
                  dislikes: video.dislikes || {},
                  comments: video.comments || {},
                  uploadDate: video.uploadDate
                    ? new Date(video.uploadDate)
                    : new Date(),
                  channelMobile: mobile.replace(/[_]/g, ""),
                  privacy: video.privacy || "public",
                  allowComments:
                    video.allowComments !== undefined
                      ? video.allowComments
                      : true,
                  isForKids: video.isForKids || false,
                  category: video.category || "Uncategorized",
                };
                userMobile = mobile;
                return true;
              }
              return false;
            });
          });

          if (foundVideo) {
            setVideoData(foundVideo);
            setIsYouTubeVideo(false);

            if (isLoggedIn) {
              const sanitizedMobile = userMobile.replace(/[^0-9]/g, "_");
              const videoRefPath = `videoplayer/videoUpload/${sanitizedMobile}/${foundVideo.dbKey}`;
              await update(ref(database, videoRefPath), {
                views: (foundVideo.views || 0) + 1,
              });
            }

            const likes = foundVideo.likes || {};
            const dislikes = foundVideo.dislikes || {};
            const currentUserMobile = getCurrentUser().mobile;
            const sanitizedCurrentUserMobile = currentUserMobile.replace(
              /[^0-9]/g,
              "_"
            );
            setLikeCount(Object.keys(likes).length);
            setDislikeCount(Object.keys(dislikes).length);
            setIsLiked(!!likes[sanitizedCurrentUserMobile]);
            setIsDisliked(!!dislikes[sanitizedCurrentUserMobile]);

            const comments = foundVideo.comments || {};
            // First map the comments with their basic data
            const commentList = Object.entries(comments).map(
              ([key, comment]) => ({
                id: key,
                ...comment,
                // Use displayName if available, otherwise use username
                displayName: comment.displayName || comment.username,
                // Store the original username for reference
                originalUsername: comment.username
              })
            );

            // Function to fetch user details for a comment
            const fetchUserDetails = async (mobile, currentComment) => {
              try {
                // If we already have a display name, use it
                if (currentComment?.displayName && currentComment.displayName !== mobile) {
                  return currentComment.displayName;
                }
                
                const sanitizedMobile = mobile.replace(/[^0-9]/g, "_");
                const userRef = ref(database, `videoplayer/Users/${sanitizedMobile}`);
                const snapshot = await get(userRef);
                if (snapshot.exists()) {
                  const userData = snapshot.val();
                  // Return display name in order of preference: name > username > mobile
                  return userData.name || userData.username || mobile;
                }
              } catch (error) {
                console.error('Error fetching user details:', error);
              }
              return mobile; // Fallback to mobile if no name found
            };

            // Update comments with display names
            const updatedComments = await Promise.all(
              commentList.map(async (comment) => {
                // If we already have a display name, use it
                if (comment.displayName && comment.displayName !== comment.username) {
                  return comment;
                }
                
                // If the username is a mobile number (contains only digits)
                if (/^\d+$/.test(comment.username)) {
                  const displayName = await fetchUserDetails(comment.username, comment);
                  // Only update if we got a different display name
                  if (displayName && displayName !== comment.username) {
                    return { ...comment, displayName };
                  }
                }
                return comment;
              })
            );

            // Only update if there are actual changes to prevent infinite loops
            if (JSON.stringify(updatedComments) !== JSON.stringify(comments)) {
              setComments(
                updatedComments.sort(
                  (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
                )
              );
            }

            // Fetch share count
            const sanitizedChannelMobile = foundVideo.channelMobile.replace(/[_]/g, "_");
            const shareRef = ref(
              database,
              `videoplayer/videoUpload/${sanitizedChannelMobile}/${foundVideo.dbKey}/share`
            );
            const shareSnapshot = await get(shareRef);
            if (shareSnapshot.exists()) {
              setShareCount(Object.keys(shareSnapshot.val()).length);
            } else {
              setShareCount(0);
            }

            const sanitizedMobile = userMobile.replace(/[_]/g, "_");
            const profileRef = ref(
              database,
              `videoplayer/UserProfile/${sanitizedMobile}`
            );
            const userRef = ref(
              database,
              `videoplayer/Users/${sanitizedMobile}`
            );
            const userPointsRef = ref(
              database,
              `videoplayer/Users/${sanitizedCurrentUserMobile}`
            );
            const [profileSnapshot, userSnapshot, pointsSnapshot] =
              await Promise.all([
                get(profileRef),
                get(userRef),
                isLoggedIn ? get(userPointsRef) : Promise.resolve(null),
              ]);

            setUserProfile({
              profilePic: profileSnapshot.exists()
                ? profileSnapshot.val().profilePic
                : null,
              name: userSnapshot.exists()
                ? userSnapshot.val().name || userSnapshot.val().fullName || foundVideo.channelMobile
                : foundVideo.channelMobile,
            });

            if (isLoggedIn && pointsSnapshot.exists()) {
              setPointsEarned(pointsSnapshot.val().points || 0);
            } else if (isLoggedIn) {
              await update(userPointsRef, { points: 0 });
              setPointsEarned(0);
            }

            const subscriptionsRef = ref(
              database,
              `videoplayer/subscriptions/${sanitizedMobile}`
            );
            const subscriptionSnapshot = await get(subscriptionsRef);
            let subscriberList = [];
            let count = 0;
            if (subscriptionSnapshot.exists()) {
              const subscriptions = subscriptionSnapshot.val();
              subscriberList = Object.entries(subscriptions).map(
                ([key, sub]) => ({
                  mobile: sub.mobile,
                  username: sub.username,
                })
              );
              count = subscriberList.length;
              if (subscriptions[sanitizedCurrentUserMobile]) {
                setIsSubscribed(true);
              }
            }
            setSubscriberCount(count);
            setSubscribers(subscriberList);

            // Fetch ads for this video
            const adsRef = ref(
              database,
              `videoplayer/videoUpload/${sanitizedMobile}/${foundVideo.dbKey}/ads`
            );
            const adsSnap = await get(adsRef);
            if (adsSnap.exists()) {
              const adsArr = Object.values(adsSnap.val()).sort(
                (a, b) => (a.adId || 0) - (b.adId || 0)
              );
              setAds(adsArr);
              // console.log('Fetched ads for video:', foundVideo.id, adsArr);
            } else {
              setAds([]);
              // console.log('No ads found for video:', foundVideo.id);
            }
          } else {
            const details = await fetchYouTubeVideoDetails(videoId);
            if (details) {
              setVideoData({
                id: videoId,
                type: "youtube_video",
                title: details.title,
                description: details.description,
                thumbnail: details.thumbnail,
                videoURL: `https://www.youtube.com/watch?v=${videoId}`,
                views: details.viewCount,
                uploadDate: new Date(details.publishedAt),
                channelMobile: details.channelTitle,
                channelId: details.channelId,
                channelSubscribers: details.channelSubscribers,
                channelProfile: details.channelProfile,
                privacy: "public",
                allowComments: true,
                isForKids: false,
                category: "YouTube",
              });
              setUserProfile({
                profilePic: details.channelProfile,
                username: details.channelTitle,
              });
              setIsYouTubeVideo(true);
              setYoutubeVideoId(videoId);
              setLikeCount(0);
              setDislikeCount(0);
              setIsLiked(false);
              setIsDisliked(false);
              setComments([]);
              setSubscriberCount(details?.channelSubscribers || 0);
              setSubscribers([]);
            } else {
              setError();
            }
          }
        } else {
          setError("No videos available");
        }
      } catch (err) {
        // console.error('Error fetching video:', err);
        setError("Failed to load video. Please try again later.");
        toast.error("Failed to load video.");
      }
    };

    fetchVideoData();
  }, [videoId, isLoggedIn]);

  // Fetch photo data if isPhoto
  useEffect(() => {
    if (!isPhoto) return;
    const fetchPhoto = async () => {
      try {
        const photosRef = ref(database, "videoplayer/photoUpload");
        const snapshot = await get(photosRef);
        let foundPhoto = null;
        let userMobile = "";
        if (snapshot.exists()) {
          Object.entries(snapshot.val()).some(([mobile, userPhotos]) => {
            return Object.entries(userPhotos).some(([photoKey, photo]) => {
              // Always prefer matching by photo.id
              // Match by id
              if (photo.id === videoId) {
                foundPhoto = {
                  id: photo.id,
                  dbKey: photoKey,
                  type: "photo",
                  title: photo.title || "Untitled Photo",
                  description: photo.description || "",
                  photoURL: photo.photoURL || "",
                  thumbnail: photo.photoURL || "",
                  uploadDate: photo.uploadDate
                    ? new Date(photo.uploadDate)
                    : new Date(),
                  channelMobile: mobile.replace(/[_]/g, ""),
                  category: photo.category || "Uncategorized",
                  status: photo.status,
                };
                userMobile = mobile;
                return true;
              }
              // Fallback: match by dbKey
              if (photoKey === videoId) {
                foundPhoto = {
                  id: photoKey,
                  dbKey: photoKey,
                  type: "photo",
                  title: photo.title || "Untitled Photo",
                  description: photo.description || "",
                  photoURL: photo.photoURL || "",
                  thumbnail: photo.photoURL || "",
                  uploadDate: photo.uploadDate
                    ? new Date(photo.uploadDate)
                    : new Date(),
                  channelMobile: mobile.replace(/[_]/g, ""),
                  category: photo.category || "Uncategorized",
                  status: photo.status,
                };
                userMobile = mobile;
                return true;
              }
              // Fallback: match by storage file name in photoURL
              if (photo.photoURL && photo.photoURL.includes(videoId)) {
                foundPhoto = {
                  id: photoKey,
                  dbKey: photoKey,
                  type: "photo",
                  title: photo.title || "Untitled Photo",
                  description: photo.description || "",
                  photoURL: photo.photoURL || "",
                  thumbnail: photo.photoURL || "",
                  uploadDate: photo.uploadDate
                    ? new Date(photo.uploadDate)
                    : new Date(),
                  channelMobile: mobile.replace(/[_]/g, ""),
                  category: photo.category || "Uncategorized",
                  status: photo.status,
                };
                userMobile = mobile;
                return true;
              }
              return false;
            });
          });
        } else {
        }
        if (foundPhoto) {
          setPhotoData(foundPhoto);
          // Fetch uploader profile
          const sanitizedMobile = userMobile.replace(/[_]/g, "_");
          const profileRef = ref(
            database,
            `videoplayer/UserProfile/${sanitizedMobile}`
          );
          const usernameRef = ref(
            database,
            `videoplayer/Users/${sanitizedMobile}/username`
          );
          const [profileSnapshot, usernameSnapshot] = await Promise.all([
            get(profileRef),
            get(usernameRef),
          ]);
          setUserProfile({
            profilePic: profileSnapshot.exists()
              ? profileSnapshot.val().profilePic
              : null,
            username: usernameSnapshot.exists()
              ? usernameSnapshot.val()
              : foundPhoto.channelMobile,
          });

          // Fetch photo likes and comments
          if (isLoggedIn) {
            const currentUser = getCurrentUser();
            const sanitizedCurrentUserMobile = currentUser.mobile.replace(
              /[^0-9]/g,
              "_"
            );

            // Fetch likes
            const likesRef = ref(
              database,
              `videoplayer/photoUpload/${sanitizedMobile}/${foundPhoto.dbKey}/likes`
            );
            const likesSnapshot = await get(likesRef);
            if (likesSnapshot.exists()) {
              const likesData = likesSnapshot.val();
              setPhotoLikes(likesData);
              setPhotoLikeCount(Object.keys(likesData).length);
              setHasLikedPhoto(!!likesData[sanitizedCurrentUserMobile]);
            }

            // Fetch comments
            const commentsRef = ref(
              database,
              `videoplayer/photoUpload/${sanitizedMobile}/${foundPhoto.dbKey}/comments`
            );
            const commentsSnapshot = await get(commentsRef);
            if (commentsSnapshot.exists()) {
              const commentsData = commentsSnapshot.val();
              const commentsArray = Object.entries(commentsData).map(
                ([key, comment]) => ({
                  id: key,
                  ...comment,
                })
              );
              setPhotoComments(
                commentsArray.sort(
                  (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
                )
              );
              setPhotoCommentCount(commentsArray.length);
            }
          }
        } else {
          setPhotoData(null);
        }
      } catch (err) {
        setError("Failed to load photo. Please try again later.");
      }
    };
    fetchPhoto();
  }, [isPhoto, videoId, isLoggedIn]);

  // Fetch commission config for photo view with real-time updates
  useEffect(() => {
    if (!isPhoto) return;

    const fetchCommission = async () => {
      try {
        const commissionRef = ref(database, "videoplayer/Commissions/1");
        const commissionSnap = await get(commissionRef);
        if (commissionSnap.exists()) {
          const commissionData = commissionSnap.val();
          setCommissionConfig(commissionData);
        } else {
          setCommissionConfig({
            photoTime: 0,
            photoPoints: 0,
            watchVideoPoints: 0,
            watchVideoTime: 0,
            youtubePoints: 0,
            youtubeTime: 0,
            videoPoints: 0,
            instagramPoints: 0,
            instagramTime: 0,
            facebookPoints: 0,
            facebookTime: 0,
            googlePoints: 0,
            googleTime: 0,
          });
        }
      } catch (err) {
        // console.error('Error fetching commission config for photo:', err);
        toast.error("Failed to load photo reward configuration.");
      }
    };

    fetchCommission();

    // Set up real-time listener for commission updates
    const commissionRef = ref(database, "videoplayer/Commissions/1");
    const unsubscribeCommission = onValue(
      commissionRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const commissionData = snapshot.val();
          setCommissionConfig(commissionData);
        }
      },
      (error) => {
        // console.error('Error in real-time commission listener:', error);
      }
    );

    return () => {
      unsubscribeCommission();
    };
  }, [isPhoto]);

  // Watch time tracking and points awarding
  useEffect(() => {
    if (!isLoggedIn || !videoData || !commissionConfig || isPhoto) return;

    let intervalId;
    const contentType = isYouTubeVideo ? "youtube" : "video";
    const timeKey = isYouTubeVideo ? "youtubeTime" : "watchVideoTime";
    const pointsKey = isYouTubeVideo ? "youtubePoints" : "watchVideoPoints";
    const requiredTimeMinutes = commissionConfig[timeKey] || 0;
    const requiredTimeSeconds = requiredTimeMinutes * 60; // Convert minutes to seconds
    const pointsToAdd = commissionConfig[pointsKey] || 0;

    intervalId = setInterval(() => {
      setWatchTimeSeconds((prev) => {
        const newTime = prev[contentType] + 1;
        // Award points every interval
        if (
          requiredTimeSeconds > 0 &&
          pointsToAdd > 0 &&
          newTime % requiredTimeSeconds === 0
        ) {
          awardPoints(pointsToAdd, contentType, "Watch Reward");
        }
        return { ...prev, [contentType]: newTime };
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isLoggedIn, videoData, commissionConfig, isPhoto, isYouTubeVideo]);

  // Photo watch time tracking and points awarding
  useEffect(() => {
    if (!isLoggedIn || !isPhoto || !photoData || !commissionConfig) return;

    const requiredTimeMinutes = commissionConfig.photoTime || 0;
    const requiredTimeSeconds = requiredTimeMinutes * 60;
    const pointsToAdd = commissionConfig.photoPoints || 0;

    let intervalId = setInterval(() => {
      setPhotoWatchTimeSeconds((prev) => {
        const newTime = prev + 1;
        // Award points every interval
        if (
          requiredTimeSeconds > 0 &&
          pointsToAdd > 0 &&
          newTime % requiredTimeSeconds === 0
        ) {
          awardPoints(pointsToAdd, "photo", "Photo Watch Reward");
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isLoggedIn, isPhoto, photoData, commissionConfig]);

  // Handle video playback
  useEffect(() => {
    if (!isLoggedIn || !videoData || isYouTubeVideo) return;
    if (!videoRef.current) return;
    videoRef.current.muted = isMuted;
    videoRef.current.play().catch((err) => {
      // console.error('Auto-play failed:', err);
      setIsMuted(true);
      if (videoRef.current) {
        videoRef.current.muted = true;
        videoRef.current.play();
      }
    });
  }, [videoData, isMuted, isLoggedIn, isYouTubeVideo]);

  // Show ad overlay when video is ready and ads exist
  useEffect(() => {
    if (!videoData || isYouTubeVideo || !ads.length || adStarted) return;
    const ad = ads[0];
    let triggerSec = 0;
    if (ad.durationType === "minutes")
      triggerSec = parseInt(ad.durationValue, 10) * 60;
    else triggerSec = parseInt(ad.durationValue, 10);
    triggerSec = isNaN(triggerSec) ? 30 : triggerSec;
    // Listen for main video time update
    const checkAdTrigger = () => {
      if (videoRef.current && !adStarted) {
        if (videoRef.current.currentTime >= triggerSec) {
          // Save the current time right before pausing
          const pausedTime = videoRef.current.currentTime;
          setMainVideoTime(pausedTime);
          videoRef.current.pause();
          setCurrentAd(ad);
          setShowAd(true);
          setAdStarted(true);
        } else {
          requestAnimationFrame(checkAdTrigger);
        }
      }
    };
    if (videoRef.current) {
      requestAnimationFrame(checkAdTrigger);
    }
    return () => {};
  }, [videoData, isYouTubeVideo, ads.length, adStarted]);

  // When ad ends or is skipped, always resume from saved time
  const handleSkipAd = () => {
    setShowAd(false);
    setAdProgress(100);
    setCanSkipAd(false);
    setSkipCountdown(0);
    clearInterval(adTimerRef.current);
    clearInterval(skipTimerRef.current);
    if (videoRef.current) {
      videoRef.current.currentTime = mainVideoTime;
      videoRef.current.play();
    }
  };

  // When ad starts, handle ad playback, skip, and progress
  useEffect(() => {
    if (!showAd || !currentAd) return;
    setCanSkipAd(false);
    setSkipCountdown(10);
    setAdProgress(0);
    setAdPlaybackTime(0);
    // Ad duration (for progress bar)
    let adDurationSec = 0;
    if (currentAd.adType === "video") {
      adDurationSec = Math.ceil(currentAd.adLength || 30); // fallback 30s
    } else {
      adDurationSec = 15; // show image ad for 15s
    }
    let elapsed = 0;
    adTimerRef.current = setInterval(() => {
      elapsed += 1;
      setAdPlaybackTime(elapsed);
      setAdProgress((elapsed / adDurationSec) * 100);
      if (elapsed >= adDurationSec) {
        setShowAd(false);
        setAdProgress(100);
        setSkipCountdown(0);
        setCanSkipAd(false);
        clearInterval(adTimerRef.current);
        clearInterval(skipTimerRef.current);
        if (videoRef.current) {
          videoRef.current.currentTime = mainVideoTime;
          videoRef.current.play();
        }
      }
    }, 1000);
    // Show skip button after 10 seconds, with countdown
    let skipCount = 10;
    setSkipCountdown(skipCount);
    skipTimerRef.current = setInterval(() => {
      skipCount -= 1;
      setSkipCountdown(skipCount);
      if (skipCount <= 0) {
        setCanSkipAd(true);
        clearInterval(skipTimerRef.current);
      }
    }, 1000);
    return () => {
      clearInterval(adTimerRef.current);
      clearInterval(skipTimerRef.current);
    };
  }, [showAd, currentAd, mainVideoTime]);

  // Fetch related videos
  useEffect(() => {
    const fetchRelated = async () => {
      let all = [];
      if (isYouTubeVideo) {
        // Do not fetch or show related videos for YouTube videos
        setRelatedVideos([]);
        return;
      } else {
        const videosRef = ref(database, "videoplayer/videoUpload");
        const snapshot = await get(videosRef);
        if (snapshot.exists()) {
          Object.entries(snapshot.val()).forEach(([mobile, userVideos]) => {
            Object.entries(userVideos).forEach(([videoKey, video]) => {
              const generatedId =
                video.id ||
                `${mobile.replace(/[^0-9]/g, "_")}_${new Date(
                  video.uploadDate || Date.now()
                )
                  .getTime()
                  .toString(36)}`;
              // Only include videos with status === 'approved'
              if (generatedId !== videoId && video.status === "approved") {
                all.push({
                  id: generatedId,
                  title: video.title || "Untitled Video",
                  thumbnail:
                    video.thumbnailURL ||
                    "https://via.placeholder.com/320x180?text=No+Thumbnail",
                  channelMobile: mobile.replace(/[_]/g, ""),
                  type: "video",
                });
              }
            });
          });
        }
      }
      setRelatedVideos(all.slice(0, 8));
    };
    fetchRelated();
  }, [isYouTubeVideo, youtubeVideoId, videoId]);

  const handleRelatedVideoClick = (id) => {
    navigate(`/video/${id}`);
  };

  const handleEdit = () => {
    navigate(`/edit-video/${videoId}`);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleDownload = async () => {
    if (!isLoggedIn) {
      redirectToLogin();
      return;
    }
    if (isYouTubeVideo) {
      toast.error("Downloading YouTube videos is not supported.");
      return;
    }
    try {
      const a = document.createElement("a");
      a.href = videoData.videoURL;
      a.download = videoData.title || "video";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      // console.error('Error downloading video:', err);
      toast.error("Failed to download video.");
    }
  };

  const handleShare = async () => {
    if (!isLoggedIn) {
      redirectToLogin();
      return;
    }
    if (isYouTubeVideo) {
      toast.error("Downloading YouTube videos is not supported.");
      return;
    }
    try {
      // const shareUrl = `${window.location.origin}/video/`;
       const shareUrl = `${window.location.origin}`;
      if (navigator.share) {
        await navigator.share({
          title: videoData.title || "Check out this video",
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied to clipboard!");
      }
      // Store share info in the database in sequence
      if (!isYouTubeVideo && videoData && isLoggedIn) {
        const user = getCurrentUser();
        const { mobile, name, surname } = user;
        // Combine name and surname for username
        let username = name || "";
        if (surname) username = username ? `${username} ${surname}` : surname;
        if (!username) username = mobile;
        const sanitizedChannelMobile = videoData.channelMobile.replace(/[_]/g, "_");
        const shareRef = ref(
          database,
          `videoplayer/videoUpload/${sanitizedChannelMobile}/${videoData.dbKey}/share`
        );
        // Get current shares to determine the next index
        const shareSnapshot = await get(shareRef);
        let nextIndex = 0;
        if (shareSnapshot.exists()) {
          nextIndex = Object.keys(shareSnapshot.val()).length;
        }
        // Save with sequence key
        const newShareRef = ref(
          database,
          `videoplayer/videoUpload/${sanitizedChannelMobile}/${videoData.dbKey}/share/${nextIndex}`
        );
        await update(newShareRef, {
          mobile,
          username,
          sharedAt: new Date().toISOString(),
        });
        setShareCount((prev) => prev + 1);
      }
    } catch (err) {
      // console.error('Error sharing video:', err);
      toast.error("Failed to share video.");
    }
  };

  const handleSubscribe = async () => {
    if (!isLoggedIn) {
      redirectToLogin();
      return;
    }
    if (isYouTubeVideo) {
      toast.error("Subscribing to YouTube channels is not supported in-app.");
      return;
    }
    try {
      const { mobile, username } = getCurrentUser();
      const sanitizedCurrentUserMobile = mobile.replace(/[^0-9]/g, "_");
      const sanitizedChannelMobile = videoData.channelMobile.replace(
        /[_]/g,
        "_"
      );
      const subscriptionsRef = ref(
        database,
        `videoplayer/subscriptions/${sanitizedChannelMobile}/${sanitizedCurrentUserMobile}`
      );

      if (isSubscribed) {
        await remove(subscriptionsRef);
        setIsSubscribed(false);
        setSubscriberCount((prev) => prev - 1);
        setSubscribers((prev) => prev.filter((sub) => sub.mobile !== mobile));
        toast.success("Unsubscribed successfully!");
      } else {
        await update(subscriptionsRef, {
          mobile,
          // username,
          subscribedAt: new Date().toISOString(),
        });
        setIsSubscribed(true);
        setSubscriberCount((prev) => prev + 1);
        setSubscribers((prev) => [...prev, { mobile, username }]);
        toast.success("Subscribed successfully!");
      }
    } catch (err) {
      // console.error('Error handling subscription:', err);
      toast.error("Failed to update subscription status.");
    }
  };

  const handleLike = async () => {
    if (!isLoggedIn) {
      redirectToLogin();
      return;
    }
    if (isYouTubeVideo) {
      toast.error("Liking YouTube videos is not supported in-app.");
      return;
    }
    try {
      const { mobile } = getCurrentUser();
      const sanitizedMobile = mobile.replace(/[^0-9]/g, "_");
      const sanitizedChannelMobile = videoData.channelMobile.replace(
        /[^0-9]/g,
        "_"
      );
      const likesRef = ref(
        database,
        `videoplayer/videoUpload/${sanitizedChannelMobile}/${videoData.dbKey}/likes/${sanitizedMobile}`
      );
      const dislikesRef = ref(
        database,
        `videoplayer/videoUpload/${sanitizedChannelMobile}/${videoData.dbKey}/dislikes/${sanitizedMobile}`
      );

      if (isLiked) {
        await remove(likesRef);
        setIsLiked(false);
        setLikeCount((prev) => prev - 1);
        toast.success("Like removed!");
      } else {
        if (isDisliked) {
          await remove(dislikesRef);
          setIsDisliked(false);
          setDislikeCount((prev) => prev - 1);
        }
        await update(likesRef, {
          timestamp: new Date().toISOString(),
        });
        setIsLiked(true);
        setLikeCount((prev) => prev + 1);
        toast.success("Video liked!");
      }
    } catch (err) {
      // console.error('Error handling like:', err);
      toast.error("Failed to update like status.");
    }
  };

  const handleDislike = async () => {
    if (!isLoggedIn) {
      redirectToLogin();
      return;
    }
    if (isYouTubeVideo) {
      toast.error("Disliking YouTube videos is not supported in-app.");
      return;
    }
    try {
      const { mobile } = getCurrentUser();
      const sanitizedMobile = mobile.replace(/[^0-9]/g, "_");
      const sanitizedChannelMobile = videoData.channelMobile.replace(
        /[^0-9]/g,
        "_"
      );
      const likesRef = ref(
        database,
        `videoplayer/videoUpload/${sanitizedChannelMobile}/${videoData.dbKey}/likes/${sanitizedMobile}`
      );
      const dislikesRef = ref(
        database,
        `videoplayer/videoUpload/${sanitizedChannelMobile}/${videoData.dbKey}/dislikes/${sanitizedMobile}`
      );

      if (isDisliked) {
        await remove(dislikesRef);
        setIsDisliked(false);
        setDislikeCount((prev) => prev - 1);
        toast.success("Dislike removed!");
      } else {
        if (isLiked) {
          await remove(likesRef);
          setIsLiked(false);
          setLikeCount((prev) => prev - 1);
        }
        await update(dislikesRef, {
          timestamp: new Date().toISOString(),
        });
        setIsDisliked(true);
        setDislikeCount((prev) => prev + 1);
        toast.success("Video disliked!");
      }
    } catch (err) {
      // console.error('Error handling dislike:', err);
      toast.error("Failed to update dislike status.");
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!isLoggedIn) {
      redirectToLogin();
      return;
    }
    if (!newComment.trim()) return;
    if (isYouTubeVideo) {
      toast.error("Commenting on YouTube videos is not supported in-app.");
      return;
    }

    try {
      const currentUser = getCurrentUser();
      const { mobile, username } = currentUser;
      const sanitizedChannelMobile = videoData.channelMobile.replace(
        /[_]/g,
        "_"
      );
      const commentsRef = ref(
        database,
        `videoplayer/videoUpload/${sanitizedChannelMobile}/${videoData.dbKey}/comments`
      );
      
      // Get the user's display name from their profile
      const userProfileRef = ref(database, `videoplayer/UserProfile/${mobile.replace(/[^0-9]/g, '_')}`);
      const userProfileSnap = await get(userProfileRef);
      const userData = userProfileSnap.exists() ? userProfileSnap.val() : {};
      
      // Also check the Users node for name
      const userRef = ref(database, `videoplayer/Users/${mobile.replace(/[^0-9]/g, '_')}`);
      const userSnap = await get(userRef);
      const userDetail = userSnap.exists() ? userSnap.val() : {};
      
      // Determine the best available name
      const displayName = userData.name || userDetail.name || userDetail.fullName || username || mobile;
      
      const newCommentRef = push(commentsRef);
      const commentData = {
        text: newComment.trim().replace(/[<>\&]/g, ""),
        username: displayName,
        displayName: displayName, // Store display name separately
        mobile,
        timestamp: new Date().toISOString(),
      };

      await update(newCommentRef, commentData);
      
      // Add the comment to the local state with the display name immediately
      setComments((prev) => [
        { 
          id: newCommentRef.key, 
          ...commentData,
          // Ensure we have the display name for immediate display
          originalUsername: displayName 
        },
        ...prev,
      ]);
      
      setNewComment("");
      commentInputRef.current.focus();
      toast.success("Comment posted successfully!");
    } catch (err) {
      // console.error('Error posting comment:', err);
      toast.error("Failed to post comment.");
    }
  };

  const awardPoints = async (points, type, giftName) => {
    if (!points || points <= 0) return;
    const { mobile } = getCurrentUser();
    const sanitizedMobile = mobile.replace(/[^0-9]/g, "_");
    const userPointsRef = ref(database, `videoplayer/Users/${sanitizedMobile}`);

    try {
      // Update total points
      const pointsSnapshot = await get(userPointsRef);
      const currentPoints = pointsSnapshot.exists()
        ? pointsSnapshot.val().points || 0
        : 0;
      const newPoints = currentPoints + points;
      await update(userPointsRef, { points: newPoints });
      setPointsEarned(newPoints);

      setShowToast({ points, type });
      setTimeout(() => setShowToast(null), 3000);
      toast.success(`+${points} points for ${type}!`);
    } catch (err) {
      // console.error(`Error awarding ${points} points for ${type}:`, err);
      toast.error(`Failed to award points for ${type}.`);
    }
  };

  const redirectToLogin = () => {
    navigate("/login", { state: { from: `/video/${videoId}` } });
  };

  // Photo like handler
  // const handlePhotoLike = async () => {
  //   if (!isLoggedIn) {
  //     redirectToLogin();
  //     return;
  //   }
  //   if (!photoData) return;

  //   try {
  //     const currentUser = getCurrentUser();
  //     const sanitizedCurrentUserMobile = currentUser.mobile.replace(
  //       /[^0-9]/g,
  //       "_"
  //     );
  //     const sanitizedPhotoMobile = photoData.channelMobile.replace(
  //       /[^0-9]/g,
  //       "_"
  //     );
  //     const likeRef = ref(
  //       database,
  //       `videoplayer/photoUpload/${sanitizedPhotoMobile}/${photoData.dbKey}/likes/${sanitizedCurrentUserMobile}`
  //     );

  //     if (hasLikedPhoto) {
  //       // Unlike
  //       await remove(likeRef);
  //       setHasLikedPhoto(false);
  //       setPhotoLikeCount((prev) => prev - 1);
  //       setPhotoLikes((prev) => {
  //         const newLikes = { ...prev };
  //         delete newLikes[sanitizedCurrentUserMobile];
  //         return newLikes;
  //       });
  //       toast.success("Like removed!");
  //     } else {
  //       // Like
  //       await update(likeRef, {
  //         timestamp: new Date().toISOString(),
  //       });
  //       setHasLikedPhoto(true);
  //       setPhotoLikeCount((prev) => prev + 1);
  //       setPhotoLikes((prev) => ({
  //         ...prev,
  //         [sanitizedCurrentUserMobile]: { timestamp: new Date().toISOString() },
  //       }));
  //       toast.success("Photo liked!");
  //     }
  //   } catch (err) {
  //     // console.error('Error handling photo like:', err);
  //     toast.error("Failed to update like status.");
  //   }
  // };

  // Photo comment handler
  // const handlePhotoComment = async (e) => {
  //   e.preventDefault();
  //   if (!isLoggedIn) {
  //     redirectToLogin();
  //     return;
  //   }
  //   if (!newPhotoComment.trim() || !photoData) return;

  //   try {
  //     const currentUser = getCurrentUser();
  //     const sanitizedPhotoMobile = photoData.channelMobile.replace(
  //       /[^0-9]/g,
  //       "_"
  //     );
  //     const commentsRef = ref(
  //       database,
  //       `videoplayer/photoUpload/${sanitizedPhotoMobile}/${photoData.dbKey}/comments`
  //     );
  //     const newCommentRef = push(commentsRef);

  //     // Get the user's display name or fall back to username
  //     const displayName = currentUser.name || currentUser.username || currentUser.mobile;
      
  //     const commentData = {
  //       text: newPhotoComment.trim().replace(/[<>\&]/g, ""),
  //       username: displayName,
  //       mobile: currentUser.mobile,
  //       timestamp: new Date().toISOString(),
  //     };

  //     await update(newCommentRef, commentData);
  //     setPhotoComments((prev) => [
  //       { id: newCommentRef.key, ...commentData },
  //       ...prev,
  //     ]);
  //     setPhotoCommentCount((prev) => prev + 1);
  //     setNewPhotoComment("");
  //     toast.success("Comment posted successfully!");
  //   } catch (err) {
  //     // console.error('Error posting photo comment:', err);
  //     toast.error("Failed to post comment.");
  //   }
  // };

  // Photo share handler
  // const handlePhotoShare = async () => {
  //   if (!isLoggedIn) {
  //     redirectToLogin();
  //     return;
  //   }
  //   if (!photoData) return;

  //   try {
  //     const shareUrl = `${window.location.origin}/video/${videoId}?type=photo`;
  //     if (navigator.share) {
  //       await navigator.share({
  //         title: photoData.title || "Check out this photo",
  //         url: shareUrl,
  //       });
  //     } else {
  //       await navigator.clipboard.writeText(shareUrl);
  //       toast.success("Link copied to clipboard!");
  //     }
  //   } catch (err) {
  //     // console.error('Error sharing photo:', err);
  //     toast.error("Failed to share photo.");
  //   }
  // };

  const generateAvatar = (name, size = 40) => {
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEEAD",
      "#D4A5A5",
      "#9B59B6",
      "#3498DB",
      "#E74C3C",
      "#2ECC71",
    ];
    const char = name?.charAt(0)?.toUpperCase() || "A";
    const color = colors[char.charCodeAt(0) % colors.length];

    return (
      <div
        className="flex items-center justify-center rounded-full text-white font-semibold"
        style={{
          width: `${size}px`,
          height: `${size}px`,
          backgroundColor: color,
          fontSize: `${size * 0.5}px`,
        }}
      >
        {char}
      </div>
    );
  };

  const getRelativeTimeAgo = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - new Date(date)) / 1000);
    if (diffInSeconds < 60)
      return `${diffInSeconds} second${diffInSeconds !== 1 ? "s" : ""} ago`;
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60)
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24)
      return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30)
      return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12)
      return `${diffInMonths} month${diffInMonths !== 1 ? "s" : ""} ago`;
    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} year${diffInYears !== 1 ? "s" : ""} ago`;
  };

  const formatWatchTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")} min`;
  };

  // On close, clear sessionStorage and navigate back
  const handleClose = () => {
    sessionStorage.removeItem("lastPhoto");
    navigate(-1);
  };

  useEffect(() => {
    if (!comments.length) return;
    const interval = setInterval(() => {
      setCommentCycleIndex((prev) => (prev + 2) % comments.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [comments]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [videoId]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && videoRef.current) {
        videoRef.current.pause();
      } else if (!document.hidden && videoRef.current && isLoggedIn && !isYouTubeVideo) {
        videoRef.current.play().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isLoggedIn, isYouTubeVideo]);

  if (error || (!videoData && !isPhoto)) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="text-red-500 dark:text-red-400 text-xl font-medium">
          {isPhoto ? "No photo found." : error || ""}
        </div>
      </main>
    );
  }

  if (isPhoto) {
    // Use the persisted photo if available, else fallback to photoData
    const photoToShow = persistedPhoto || photoData;
    if (!photoToShow || !photoToShow.photoURL) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f3f4f6",
          }}
        >
          <div
            style={{ color: "#ef4444", fontSize: "1.5rem", fontWeight: 600 }}
          >
            No photo data found. Please click a photo from the gallery.
          </div>
        </div>
      );
    }
    return (
      <div
        style={{
          position: "relative",
          minHeight: "100vh",
          background: "#f3f4f6",
        }}
      >
        {/* Close (X) button */}
        <button
          onClick={handleClose}
          style={{
            position: "absolute",
            top: 24,
            right: 24,
            zIndex: 1001,
            background: "rgba(255,255,255,0.85)",
            border: "none",
            borderRadius: "50%",
            width: 44,
            height: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            cursor: "pointer",
          }}
          aria-label="Close photo"
        >
          <span
            style={{
              fontSize: 28,
              color: "#222",
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            &times;
          </span>
        </button>
        <img
          src={photoToShow.photoURL}
          alt={photoToShow.title || "Photo"}
          // onError={() => { console.error('[PHOTO ERROR] Image failed to load:', photoToShow.photoURL); }}
          style={{
            border: "3px solid #2563eb",
            maxWidth: "95vw",
            maxHeight: "80vh",
            display: "block",
            margin: "60px auto 24px auto",
            borderRadius: "18px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            background: "#fff",
            objectFit: "contain",
          }}
        />
        <main className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-center mb-4">
              {photoToShow.title}
            </h1>
            {photoToShow.description && (
              <div className="mb-4 p-4 bg-white dark:bg-gray-800 rounded-xl shadow text-center">
                <p className="text-gray-700 dark:text-gray-200 text-base sm:text-lg">
                  {photoToShow.description}
                </p>
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  const hasProfilePic =
    userProfile.profilePic &&
    !userProfile.profilePic.includes("placeholder.com");
  const contentType = isYouTubeVideo ? "youtube" : "video";
  const currentWatchTimeSeconds = watchTimeSeconds[contentType] || 0;
  const isOwnVideo =
    isLoggedIn &&
    !isYouTubeVideo &&
    getCurrentUser().mobile === videoData.channelMobile;

  return (
    <main className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 mb-6 transition-colors duration-300"
        >
          <FiArrowLeft className="mr-2" size={20} />
          <span className="font-medium">Back to Videos</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {/* Video Player */}
            <div
              ref={videoPlayerContainerRef}
              className="relative w-full aspect-video bg-black"
            >
              {isYouTubeVideo ? (
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&mute=${
                    isMuted ? 1 : 0
                  }`}
                  title={videoData.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full object-contain"
                />
              ) : (
                <video
                  ref={videoRef}
                  src={videoData.videoURL}
                  className={`w-full h-full object-contain ${
                    showAd ? "hidden" : "block"
                  }`}
                  controls={isLoggedIn}
                  autoPlay={isLoggedIn}
                  muted={isMuted}
                  poster={videoData.thumbnail}
                  onError={(e) => {
                    // console.error('Video playback error:', e);
                    setError("Failed to load video");
                  }}
                />
              )}
              {/* Ad video: only visible during ad */}
              {showAd && currentAd && (
                <>
                  <video
                    src={currentAd.adUrl}
                    autoPlay
                    className="w-full h-full object-contain absolute top-0 left-0"
                    style={{ background: "black" }}
                  />
                  {/* Skip button and progress overlays */}
                  <div className="absolute bottom-0 left-0 w-full h-2 bg-gray-200">
                    <div
                      className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 transition-all duration-300"
                      style={{ width: `${adProgress}%` }}
                    />
                  </div>
                  <div className="absolute top-3 right-3">
                    {!canSkipAd && skipCountdown > 0 ? (
                      <div className="bg-black/70 text-white px-4 py-1 rounded-full font-semibold text-sm animate-fadeIn">
                        Skip in {skipCountdown}s
                      </div>
                    ) : canSkipAd ? (
                      <button
                        onClick={handleSkipAd}
                        className="px-5 py-2 bg-black/70 text-white border border-gray-300 rounded-md font-medium shadow-sm hover:bg-gray-100 transition-colors animate-fadeIn"
                      >
                        Skip Ad
                      </button>
                    ) : null}
                  </div>
                </>
              )}
            </div>

            {/* Video Title and Channel Info */}
            <div className="mt-4">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-tight">
                {videoData.title}
              </h1>
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    {hasProfilePic ? (
                      <img
                        src={userProfile.profilePic}
                        alt={userProfile.username}
                        className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                      />
                    ) : (
                      generateAvatar(userProfile.username, 40)
                    )}
                    {isYouTubeVideo && videoData.channelId && (
                      <a
                        href={`https://www.youtube.com/channel/${videoData.channelId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute -bottom-1 -right-1 bg-red-600 text-white text-xs px-2 py-0.5 rounded-full flex items-center"
                      >
                        <FaYoutube size={10} />
                      </a>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {userProfile.name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {Number(subscriberCount).toLocaleString()} subscribers
                    </p>
                  </div>
                </div>
                {!isYouTubeVideo &&
                  (isOwnVideo ? (
                    <button
                      onClick={handleEdit}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md font-medium transition-all duration-300 hover:bg-blue-700"
                    >
                      <FiEdit className="mr-2" size={16} />
                      Edit
                    </button>
                  ) : (
                    <button
                      onClick={handleSubscribe}
                      className={`flex items-center px-4 py-2 rounded-md font-medium transition-all duration-300 ${
                        isSubscribed
                          ? "bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200"
                          : "bg-red-600 text-white hover:bg-red-700"
                      }`}
                    >
                      <FiUsers className="mr-2" size={16} />
                      {isSubscribed ? "Subscribed" : "Subscribe"}
                    </button>
                  ))}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mt-2">
                <span>
                  {videoData.views
                    ? Number(videoData.views).toLocaleString()
                    : 0}{" "}
                  views
                </span>
                <span>•</span>
                <span>
                  {videoData.uploadDate
                    ? new Date(videoData.uploadDate).toLocaleDateString()
                    : ""}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            {!isYouTubeVideo && (
              <div className="flex items-center gap-3 mt-4 flex-wrap">
                <button
                  onClick={handleLike}
                  className={`flex items-center px-4 py-2 rounded-md font-medium transition-all duration-300 ${
                    isLiked
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
                  }`}
                >
                  <FiThumbsUp className="mr-2" size={16} />
                  {likeCount}
                </button>
                <button
                  onClick={handleDislike}
                  className={`flex items-center px-4 py-2 rounded-md font-medium transition-all duration-300 ${
                    isDisliked
                      ? "bg-red-600 text-white"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
                  }`}
                >
                  <FiThumbsDown className="mr-2" size={16} />
                  {dislikeCount}
                </button>
                <button
                  onClick={handleShare}
                  className="flex items-center px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md font-medium transition-all duration-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  <FiShare2 className="mr-2" size={16} />
                  Share
                  <span className="ml-2 text-xs text-gray-500">{shareCount}</span>
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md font-medium transition-all duration-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  <FiDownload className="mr-2" size={16} />
                  Download
                </button>
                {isYouTubeVideo && (
                  <button
                    onClick={() => window.open(videoData.videoURL, "_blank")}
                    className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md font-medium transition-all duration-300 hover:bg-red-700"
                  >
                    <FaYoutube className="mr-2" size={16} />
                    Watch on YouTube
                  </button>
                )}
              </div>
            )}

            {/* Description */}
            <div className="mt-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Description
                </h3>
                {videoData.description &&
                  videoData.description.length > 200 && (
                    <button
                      className="text-sm text-red-600 dark:text-red-400 hover:underline"
                      onClick={() => setShowFullDescription((v) => !v)}
                    >
                      {showFullDescription ? "Show Less" : "Show More"}
                    </button>
                  )}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">
                {showFullDescription ||
                !videoData.description ||
                videoData.description.length <= 200
                  ? videoData.description || "No description provided"
                  : videoData.description.slice(0, 200) + "..."}
              </p>
              {videoData.category && (
                <div className="mt-3">
                  <span className="px-3 py-1 bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-xs rounded-full">
                    #{videoData.category}
                  </span>
                </div>
              )}
            </div>

            {/* Comments Section */}
            {!isYouTubeVideo && videoData.allowComments && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Comments ({comments.length})
                </h3>
                {isLoggedIn ? (
                  <form
                    onSubmit={handleCommentSubmit}
                    className="flex items-start gap-3 mb-6"
                  >
                    <div className="flex-1">
                      <textarea
                        ref={commentInputRef}
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment..."
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                        rows="2"
                      />
                    </div>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-red-600 text-white rounded-md font-medium transition-all duration-300 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!newComment.trim()}
                    >
                      <FiSend size={16} />
                    </button>
                  </form>
                ) : (
                  <div className="mb-6 text-gray-600 dark:text-gray-300">
                    Please{" "}
                    <a
                      href="/login"
                      className="text-red-600 dark:text-red-400 hover:underline"
                    >
                      sign in
                    </a>{" "}
                    to post a comment.
                  </div>
                )}
                <div className="space-y-4">
                  {comments.length === 0 && (
                    <div className="text-gray-500 dark:text-gray-400 text-center">
                      No comments yet.
                    </div>
                  )}
                  {comments
                    .slice(commentCycleIndex, commentCycleIndex + 2)
                    .concat(
                      commentCycleIndex + 2 > comments.length
                        ? comments.slice(
                            0,
                            (commentCycleIndex + 2) % comments.length
                          )
                        : []
                    )
                    .map((comment) => (
                      <div key={comment.id} className="flex gap-3">
                        <div className="flex-shrink-0">
                          {generateAvatar(comment.displayName || comment.username, 36)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {comment.displayName || comment.username}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {getRelativeTimeAgo(comment.timestamp)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {comment.text}
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Timeline Points */}
            {isLoggedIn && (
              <div className="mt-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <FiClock className="text-red-500" size={18} />
                  <span>
                    Watch Time:{" "}
                    <span className="font-medium">
                      {formatWatchTime(currentWatchTimeSeconds)}
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm font-medium text-red-600 dark:text-red-400">
                  <span>
                    Points: <span className="font-bold">{pointsEarned}</span>
                  </span>
                  {showToast && (
                    <div className="relative">
                      <div className="absolute -top-6 bg-red-500 text-white text-xs rounded-full px-2 py-1 animate-bounce">
                        +{showToast.points} ({showToast.type})
                      </div>
                    </div>
                  )}
                </div>
                <div className="w-full sm:w-1/3">
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-red-600 transition-all duration-1000 ease-out"
                      style={{
                        width: `${(() => {
                          const requiredTimeSeconds = isYouTubeVideo
                            ? (commissionConfig?.youtubeTime || 0) * 60
                            : (commissionConfig?.watchVideoTime || 0) * 60;
                          if (!requiredTimeSeconds) return 0;
                          return Math.min(
                            100,
                            ((currentWatchTimeSeconds % requiredTimeSeconds) /
                              requiredTimeSeconds) *
                              100
                          );
                        })()}%`,
                      }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>{formatWatchTime(currentWatchTimeSeconds)}</span>
                    <span>
                      {(() => {
                        const requiredTimeSeconds = isYouTubeVideo
                          ? (commissionConfig?.youtubeTime || 0) * 60
                          : (commissionConfig?.watchVideoTime || 0) * 60;
                        return formatWatchTime(requiredTimeSeconds);
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Related Videos */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Related Videos
            </h3>
            <div className="space-y-4">
              {relatedVideos.length === 0 && (
                <div className="text-gray-500 dark:text-gray-400 text-center">
                  No related videos found.
                </div>
              )}
              {relatedVideos.map((video) => (
                <div
                  key={video.id}
                  className="cursor-pointer bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition-all duration-300"
                  onClick={() => handleRelatedVideoClick(video.id)}
                >
                  <div className="relative pt-[56.25%]">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="absolute inset-0 w-full h-full object-cover rounded-t-lg"
                    />
                  </div>
                  <div className="p-3">
                    <div className="font-medium text-sm text-gray-900 dark:text-white line-clamp-2">
                      {video.title}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {video.channelTitle || video.channelMobile}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default VideoPlay;
